
import { defineComponent } from 'vue';
import Trailer from '@/types/Trailer';
import FbxButton from '@/components/fbx-button.vue';
import FbxInput from '@/components/fbx-input.vue';
import { isNullOrUndefined } from '@/shared/helpers';
import { mapGetters } from 'vuex';
import apmTransactions from '@/libraries/apm/transactions';
import DriverLoaded from '@/components/DriverLoaded.vue';
import getTrailerAlreadyAttachedToTruck from '@/shared/mixins/GetTrailerAlreadyAttachedToTruck';
import { ActionTypes } from '@/store/actionTypes';
import checkTrailerAttachability from '@/shared/mixins/checkTrailerAttachability';
import {
    isPlateMatch,
    isVinMatch,
    trailerHasLicensePlateNumber,
    trailerHasVin,
    vinOrPlateVerificationText,
} from '@/shared/trailerIdentityVerification';

export default defineComponent({
    name: 'TrailerPlateVinVerification',
    components: {
        FbxInput,
        FbxButton,
        DriverLoaded,
    },
    data(): {
        vin: string;
        licensePlate: string;
        vinAlert: string | null;
        plateAlert: string | null;
        showLoadedTrailerModal: boolean;
        currentAttachedTrailerNumber: string | null;
        currentAttachedTrailerCompany: string | null;
    } {
        return {
            vin: '',
            licensePlate: '',
            vinAlert: null,
            plateAlert: null,
            showLoadedTrailerModal: false,
            currentAttachedTrailerNumber: null,
            currentAttachedTrailerCompany: null,
        };
    },
    props: {
        trailer: Trailer,
        proximity: Number,
    },
    computed: {
        ...mapGetters({
            isLoading: 'isLoading',
            driver: 'getDriver',
        }),
        isInvalidInput(): boolean {
            return (
                (this.licensePlate.length < 5 && this.vin.length < 5) ||
                this.vinAlert != null ||
                this.plateAlert != null
            );
        },
        continueButtonText(): string {
            return this.isLoading ? 'Continuing' : 'Continue';
        },
        vinOrPlateText(): string {
            return vinOrPlateVerificationText(
                this.hasVin,
                this.hasLicensePlateNumber
            );
        },
        cannotVerify(): boolean {
            return !this.hasLicensePlateNumber && !this.hasVin;
        },
        hasLicensePlateNumber(): boolean {
            return trailerHasLicensePlateNumber(this.trailer);
        },
        hasVin(): boolean {
            return trailerHasVin(this.trailer);
        },
    },
    mixins: [getTrailerAlreadyAttachedToTruck, checkTrailerAttachability],
    methods: {
        async fetchDriver() {
            await this.$store.dispatch(ActionTypes.FETCH_DRIVER, {
                company: this.driver.company,
                driverCode: this.driver.driverCode,
            });
        },
        hideModal() {
            this.showLoadedTrailerModal = false;
        },
        async verifyTrailer() {
            if (this.validateVin() || this.validatePlate()) {
                const available = await this.checkTrailerAttachability({
                    trailerNumber: this.trailer!.trailerNumber,
                    trailerCompany: this.trailer!.company,
                    driverId: this.driver?.id,
                });

                if (this.trailerAndDriverAreLoaded === true) {
                    const attachedTrailer =
                        await this.getTrailerAlreadyAttachedToTruck();
                    this.currentAttachedTrailerNumber =
                        attachedTrailer?.trailerNumber ?? null;
                    this.currentAttachedTrailerCompany =
                        attachedTrailer?.company ?? null;

                    this.showLoadedTrailerModal = true;
                    return;
                }

                if (available == false) {
                    this.$router.push({
                        name: 'TrailerUnavailable',
                        query: {
                            trailerNumber: this.trailer?.trailerNumber,
                            trailerCompany: this.trailer?.company,
                        },
                    });
                    return;
                }

                this.$emit('attachVerified', this.trailer);
            }
        },
        validateVin(): boolean {
            if (this.vin === '') {
                return false;
            }

            if (isVinMatch(this.trailer, this.vin)) {
                return true;
            }

            this.vinAlert = 'Incorrect VIN!';
            return false;
        },
        validatePlate(): boolean {
            if (this.licensePlate === '') {
                return false;
            }

            if (isPlateMatch(this.trailer, this.licensePlate)) {
                return true;
            }

            this.plateAlert = 'Incorrect Plate!';
            return false;
        },
        clearAlert(): void {
            this.vinAlert = null;
            this.plateAlert = null;
        },
    },
    mounted() {
        if (!isNullOrUndefined(this.trailer)) {
            apmTransactions.instrumentDriverArrivedAtVinVerification(
                this.trailer.id,
                this.proximity as number
            );
        }
    },
    watch: {
        vin() {
            this.clearAlert();
        },
        licensePlate() {
            this.clearAlert();
        },
    },
});
